import React, { useState, useEffect } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import Footer from '../navbar/Footer';

const rapportPaysData = [
  {
    "title": "Rapport Pays sur le Soutien à la Statistique - CRESS Burundi 2020-2025",
    "principalLink": "/documents/rapportPays/CRESS_2023.pdf",
    "annexeLink": null,
    "image": "assets/rapportPays/cover_rapport_pays.webp",
    "description": "Le rapport 2020-2025 révèle que le système statistique national burundais, bien que structuré, est confronté à des défis majeurs de financement (seulement 7% des fonds alloués à la production) et à une répartition déséquilibrée de ses ressources humaines et matérielles.",
    "date": "2023-12-15"
  },
   {
    title: "Rapport Pays sur le Soutien à la Statistique  CRESS  Burundi pour 2012-2018",
    link: "/documents/rapports/2.-CRESS-BURUNDI.pdf",
    description: "Entre 2012-2016, le Burundi a mobilisé 31,5 milliards FBu (19,9 millions USD) pour ses activités statistiques, dont 16,8 milliards pour l'ISTEEBU et 14,7 milliards pour les services sectoriels, avec des financements clés de la Banque Mondiale, UNICEF et autres partenaires pour des enquêtes, renforcement institutionnel et analyses.",
    image: "assets/cover/cover.jpg",
    year: 2016
  },
];

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '20px',
  backgroundColor: 'green',
  color: 'white',
  '&:hover': {
    backgroundColor: 'darkgreen',
  },
  '&:active': {
    backgroundColor: 'darkgreen',
  },
}));

function RapportPaysSoutien() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [filteredRapports, setFilteredRapports] = useState([]);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year === 'Tous') {
      setFilteredRapports([...rapportPaysData].sort((a, b) => new Date(b.date) - new Date(a.date)));
    } else {
      const filtered = rapportPaysData.filter((rapport) => {
        const rapportYear = new Date(rapport.date).getFullYear();
        return rapportYear === parseInt(year);
      }).sort((a, b) => new Date(b.date) - new Date(a.date));
      setFilteredRapports(filtered);
    }
  };

  useEffect(() => {
    handleYearChange({ target: { value: 'Tous' } });
  }, []);

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme();

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: 'green', marginBottom: '16px' }}>
            Rapports Pays sur le Soutien à la Statistique - CRESS Burundi
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Les Rapports Pays sur le Soutien à la Statistique dans le cadre du projet CRESS (Cadre Renforcé de Soutien Statistique) documentent les progrès accomplis dans le renforcement des capacités du système statistique national du Burundi. Ces rapports annuels présentent une analyse détaillée des activités menées, des résultats obtenus et des défis rencontrés dans l'amélioration de la production, diffusion et utilisation des statistiques officielles.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année"
            >
              <MenuItem value="Tous">Tous les rapports</MenuItem>
              {[...new Set(rapportPaysData.map((rapport) => new Date(rapport.date).getFullYear()))]
                .sort((a, b) => b - a)
                .map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredRapports.slice(0, visibleCount).map((rapport, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={rapport.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 141, height: 150, m: 2 }}
                        image={rapport.image}
                        alt={rapport.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: 'green' }}>
                        <a
                          href={rapport.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {<DownloadIcon />}
                          {rapport.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {rapport.description}
                      </Typography>
                      {rapport.annexeLink && (
                        <Typography variant="body2" style={{ marginBottom: '8px' }}>
                          <a
                            href={rapport.annexeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'green', fontWeight: 'bold' }}
                          >
                            Télécharger les annexes
                          </a>
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(rapport.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredRapports.length && (
            <StyledButton onClick={handleShowMore}>
              Plus de rapports
            </StyledButton>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            Ces rapports constituent un outil essentiel pour le suivi et l'évaluation des efforts de renforcement des capacités statistiques au Burundi, permettant d'ajuster les interventions en fonction des besoins identifiés et des priorités nationales.
          </Typography>
        </Container>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default RapportPaysSoutien;